import { resolveContentLink } from "@/lib/contentLink";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Calendar, ArrowRight, Clock, TrendingDown, Users, AlertTriangle, CheckCircle2, Sparkles } from "lucide-react";
import { EditableImage } from "@/components/editable/EditableImage";
import vlootschouwHero from "@/assets/images/vlootschouw-hero-photo.webp";
import grid9Nl from "@/assets/illustrations/vlootschouw-9grid-nl.webp";
import grid9En from "@/assets/illustrations/vlootschouw-9grid-en.webp";
import grid9De from "@/assets/illustrations/vlootschouw-9grid-de.webp";
import gedragNl from "@/assets/illustrations/vlootschouw-gedrag-nl.webp";
import gedragEn from "@/assets/illustrations/vlootschouw-gedrag-en.webp";
import gedragDe from "@/assets/illustrations/vlootschouw-gedrag-de.webp";
import opvolgingNl from "@/assets/illustrations/vlootschouw-opvolging-nl.webp";
import opvolgingEn from "@/assets/illustrations/vlootschouw-opvolging-en.webp";
import opvolgingDe from "@/assets/illustrations/vlootschouw-opvolging-de.webp";
import { ClientLogos } from "@/components/ClientLogos";
import { ScheduleDemo } from "@/components/sections";
import { PartnerForChange } from "@/components/sections/PartnerForChange";
import { PageContainer } from "@/components/layouts/MainLayout";
import { T, THtml } from "@/components/editable/T";
import { getLocalizedRoutePath } from "@/lib/routeUtils";
import { FeatureImageFrame } from "@/components/FeatureImageFrame";

const P = "talentReview";

const featureColors = [
  "#F3EDFD", "#FDF2ED", "#FFFBEB"];


const Vlootschouw = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language || 'nl';
  const demoPath = getLocalizedRoutePath('demo', lang);

  const features = [
  {
    titleKey: "features.row1.title",
    titleDefault: "Voorbereid met de data die er al staat",
    bodyKey: "features.row1.body",
    bodyDefault: "Evaluaties, feedback en functieprofielen vormen samen de basis van de vlootschouw, dus de voorbereiding is geen invuloefening meer.",
    imageKey: 'row1-image',
    color: featureColors[0],
    image: lang === 'de' ? gedragDe : lang === 'en' ? gedragEn : gedragNl,
    imageAlt: 'Beoordeling van gedrag en resultaten met scores van medewerker en manager',
    noFrame: true,
    demoCta: true
  },
  {
    titleKey: "features.row2.title",
    titleDefault: "Inzicht dat verder gaat dan negen vakjes",
    bodyKey: "features.row2.body",
    bodyDefault: "Skills- en potentieelinzicht per medewerker, zodat het gesprek gaat over wat iemand kan en wat er nog ontbreekt.",
    imageKey: 'row2-image',
    color: featureColors[1],
    image: lang === 'de' ? grid9De : lang === 'en' ? grid9En : grid9Nl,
    imageAlt: '9-grid met prestatie tegen potentie per medewerker',
    noFrame: true,
    demoCta: true
  },
  {
    titleKey: "features.row3.title",
    titleDefault: "Van talent review naar opvolging",
    bodyKey: "features.row3.body",
    bodyDefault: "Wat je in de sessie afspreekt, landt in ontwikkelplannen in plaats van in een bestand.",
    imageKey: 'row3-image',
    color: featureColors[2],
    image: lang === 'de' ? opvolgingDe : lang === 'en' ? opvolgingEn : opvolgingNl,
    imageAlt: 'Carrièrepad met aanbevolen vervolgstappen en te ontwikkelen skills',
    noFrame: true,
    cta: { labelKey: "features.row3.cta", labelDefault: "Ontdek ontwikkelplannen", link: resolveContentLink(`/${lang}/platform/performance-management/ontwikkelplannen/`, lang) }
  }];


  return (
    <>
      {/* Hero Section */}
      <section className="pt-[140px] pb-6 px-6 relative">
        <PageContainer className="relative z-10">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
            <Link to={`/${lang}/`} className="hover:text-primary transition-colors">
              <T page={P} k="breadcrumb.home" d="Home" />
            </Link>
            <span>›</span>
            <Link to={`/${lang}/platform/performance-management/`} className="hover:text-primary transition-colors">
              <T page={P} k="breadcrumb.parent" d="Performance Management" />
            </Link>
            <span>›</span>
            <span style={{ color: "#2A2334" }}>
              <T page={P} k="breadcrumb.current" d="Vlootschouw" />
            </span>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-12">
            <div className="animate-fade-in">
              <p className="text-lead text-muted-foreground mb-4">
                <T page={P} k="hero.subtitle" d="Talent review op platformdata" />
              </p>
              <h1 className="mb-6" style={{ color: '#2A2334' }}>
                <T page={P} k="hero.title.lead" d="Efficiënt kalibreren" />
              </h1>
              <THtml page={P} k="hero.body" d="Overzie alle informatie die je nodig hebt, en kalibreer in één sessie." as="div" className="text-lead text-muted-foreground mb-8 max-w-xl" />
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" className="text-sm px-6 py-5 rounded-full bg-[#9055EE] hover:bg-[#753DCE]" onClick={() => window.open('https://app.learned.io/register', '_blank')}>
                  <T page={P} k="hero.tryFree" d="Probeer gratis" />
                </Button>
                <Button variant="outline" size="lg" className="text-sm px-6 py-5 rounded-full bg-white hover:bg-gray-50" onClick={() => document.getElementById('schedule-demo')?.scrollIntoView({ behavior: 'smooth' })}>
                  <Calendar className="mr-2 h-4 w-4" style={{ color: '#9055EE' }} />
                  <T page={P} k="hero.planDemo" d="Plan een demo" />
                </Button>
              </div>
            </div>

            <div className="animate-fade-in relative" style={{ animationDelay: '200ms' }}>
              <EditableImage
                page={P}
                section="hero"
                imageKey="hero-image"
                src={vlootschouwHero}
                alt="Medewerker werkt geconcentreerd achter zijn bureau"
                className="w-full h-[450px] object-cover object-center rounded-2xl"
                loading="eager"
              />
              <div className="absolute -right-8 -top-5 bg-white rounded-full shadow-xl ring-[8px] ring-white/80 px-4 py-2.5 transform rotate-3 hover:rotate-0 transition-transform duration-300 hidden lg:flex items-center gap-2 opacity-0 animate-fade-float-in" style={{ animationDelay: '1100ms', animationFillMode: 'forwards' }}>
                <Clock className="w-4 h-4 text-[#9055EE]" />
                <T page={P} k="hero.cards.oneSession" d="Kalibreren in één sessie" as="span" className="text-sm font-semibold text-gray-700" />
              </div>
              <div className="absolute -right-6 bottom-24 bg-white rounded-full shadow-xl ring-[8px] ring-white/80 px-4 py-2.5 transform -rotate-2 hover:rotate-0 transition-transform duration-300 hidden lg:flex items-center gap-2 opacity-0 animate-fade-float-in" style={{ animationDelay: '1400ms', animationFillMode: 'forwards' }}>
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                <T page={P} k="hero.cards.prepared" d="Voorbereiding staat klaar" as="span" className="text-sm font-semibold text-gray-700" />
              </div>
              <div className="absolute -left-6 bottom-10 bg-white rounded-full shadow-xl ring-[8px] ring-white/80 px-4 py-2.5 transform -rotate-2 hover:rotate-0 transition-transform duration-300 hidden lg:flex items-center gap-2 opacity-0 animate-fade-float-in" style={{ animationDelay: '1700ms', animationFillMode: 'forwards' }}>
                <Sparkles className="w-4 h-4 text-[#FB9A24]" />
                <T page={P} k="hero.cards.actualInsight" d="Altijd actueel inzicht" as="span" className="text-sm font-semibold text-gray-700" />
              </div>
            </div>
          </div>
          <ClientLogos />
        </PageContainer>
      </section>

      {/* Challenge Block */}
      <section
        className="overflow-x-hidden px-4 py-20 sm:px-6"
        style={{ background: "linear-gradient(135deg, #2A2334 0%, #4A3660 50%, #2A2334 100%)" }}
      >
        <div className="mx-auto w-full max-w-5xl">
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <div className="mx-auto flex w-full max-w-sm flex-col items-center gap-3 sm:max-w-none sm:flex-row sm:flex-wrap sm:justify-center sm:gap-4">
              {[
                { icon: Clock, k: "challenges.pain1", d: "Dagen voorbereiden in Excel" },
                { icon: TrendingDown, k: "challenges.pain2", d: "Inzicht is meteen verouderd" },
                { icon: Users, k: "challenges.pain3", d: "Beslissingen op onderbuikgevoel" },
                { icon: AlertTriangle, k: "challenges.pain4", d: "Overzicht verdwijnt na de sessie" },
              ].map((point, i) => {
                const Icon = point.icon;
                return (
                  <div
                    key={i}
                    className="flex w-full items-center justify-center gap-3 rounded-full bg-white/10 px-4 py-3 backdrop-blur-sm sm:w-auto sm:px-5"
                  >
                    <Icon className="w-4 h-4 text-white/60 shrink-0" />
                    <T page={P} k={point.k} d={point.d} as="span" className="text-center text-sm leading-snug text-white/80" />
                  </div>
                );
              })}
            </div>

            <div>
              <T page={P} k="challenges.eyebrow" d="De uitdaging" as="p" className="text-xs font-semibold uppercase tracking-wider text-white/60 mb-2" />
              <h2 className="feature-section-heading text-white mb-6">
                <T page={P} k="challenges.title" d="Voorkom lange voorbereidingen voor een incompleet gesprek" />
              </h2>
              <THtml
                page={P}
                k="challenges.body1"
                d="De vlootschouw is vaak een Excel-exercitie: dagen voorbereiden, handmatig invullen, en na de sessie verdwijnt het bestand in een map. Het inzicht dat je met zoveel moeite hebt opgebouwd, is de volgende dag alweer verouderd, met als gevolg dat beslissingen over promoties, ontwikkeling en beloning toch weer leunen op geheugen en onderbuikgevoel, omdat het overzicht er niet meer is wanneer je het nodig hebt."
                as="p"
                className="text-sm text-white/80 leading-relaxed"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="pt-[60px] pb-20 px-6">
        <PageContainer>
          <div className="space-y-16">
            {features.map((feature, index) => {
              const isReversed = index % 2 === 1;
              return (
                <div
                  key={index}
                  className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center animate-fade-in"
                  style={{ animationDelay: `${index * 100}ms` }}>

                  <div className={`${isReversed ? 'lg:order-2' : 'lg:order-1'}`}>
                    <h2 className="feature-section-heading text-foreground">
                      <T page={P} k={feature.titleKey} d={feature.titleDefault} />
                    </h2>
                    <THtml page={P} k={feature.bodyKey} d={feature.bodyDefault} as="p" className="text-lead text-muted-foreground" />

                    {(feature.cta || feature.demoCta) &&
                    <Button asChild className="mt-6 rounded-full bg-[#9055EE] hover:bg-[#753DCE] text-white">
                        <Link to={demoPath}>
                          <T page={P} k="features.demoCta" d="Plan een demo" />
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Link>
                      </Button>
                    }
                  </div>
                  <div className={`${isReversed ? 'lg:order-1' : 'lg:order-2'}`}>
                    {'image' in feature && feature.image ? (
                      <EditableImage
                        page={P}
                        section="features"
                        imageKey={feature.imageKey}
                        src={feature.image}
                        alt={'imageAlt' in feature ? feature.imageAlt : ''}
                        className="w-full rounded-2xl"
                      />
                    ) : (
                      /* TODO: echte productafbeelding toevoegen (Visuals: section features) */
                      <FeatureImageFrame color={feature.color} />
                    )}
                  </div>
                </div>);

            })}
          </div>
        </PageContainer>
      </section>


      <PartnerForChange />

      <ScheduleDemo />
    </>);

};

export default Vlootschouw;
